// Чистая логика альбома вложений: область листания и последовательное скачивание.

export interface AlbumItem {
  fileId: string;
  messageId: number;
}

/** Просмотримые вложения одного сообщения; альбом — если их больше одного. */
export function albumScope<T extends AlbumItem>(all: T[], messageId: number | undefined): T[] {
  if (messageId === undefined) return [];
  const own = all.filter((x) => x.messageId === messageId);
  return own.length > 1 ? own : [];
}

export interface DownloadProgress {
  done: number;
  total: number;
  saved: number;
}

export interface DownloadResult {
  saved: number;
  total: number;
  failed: number;
}

/**
 * Последовательно качает элементы. Уже скачанные (alreadySaved) пропускает, но
 * засчитывает в итог. Ошибка одного элемента не останавливает остальные.
 * Если download вернул false (отказ политики передачи) — элемент считается неуспешным.
 */
export async function downloadSequentially<T extends { fileId: string }>(
  items: T[],
  download: (item: T, index: number) => Promise<boolean>,
  alreadySaved: ReadonlySet<string>,
  onProgress: (p: DownloadProgress) => void,
  onSaved?: (item: T) => void,
): Promise<DownloadResult> {
  let saved = 0;
  let failed = 0;
  onProgress({ done: 0, total: items.length, saved: 0 });
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (alreadySaved.has(item.fileId)) {
      saved++;
    } else {
      let ok: boolean;
      try {
        ok = await download(item, i);
      } catch {
        ok = false;
      }
      if (ok) {
        saved++;
        onSaved?.(item);
      } else {
        failed++;
      }
    }
    onProgress({ done: i + 1, total: items.length, saved });
  }
  return { saved, total: items.length, failed };
}

/** Альбом останавливается на краях; одиночные медиа сохраняют листание по ленте. */
export function mediaNavigationIndex(index: number, length: number, delta: number, album: boolean): number {
  if (index < 0 || index >= length || length < 2) return -1;
  const next = index + delta;
  if (album && (next < 0 || next >= length)) return -1;
  return ((next % length) + length) % length;
}
